import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Spinner } from "reactstrap";

import { useCompanies } from "../../../hooks/companies";
import { useToast } from "../../../hooks/toast";
import SpecificEmpresaRequiredNotice from "../../../components/SpecificEmpresaRequiredNotice";
import PageHero, { PageHeroRefreshButton } from "../../../components/PageHero";
import ShellCard from "../../../components/ShellCard";
import { AUTOPILOT_COLORS } from "../../../components/autopilot/styles";
import { getFormulario, mensagemErroParametros } from "../../../services/parametrosApi";
import { listarCertificados } from "../../../services/embarcadorCertificadoApi";
import { listarConectoresErp } from "../../../services/conectorEmbarcadorApi";
import { CONECTORES } from "./conectores";
import { statusDoConector, ROTULO_FILTRO } from "./statusDoConector";
import { statusConectorErp, cardDoCatalogo } from "./statusConectorErp";
import {
  TIPOS_SISTEMA,
  CAPACIDADES,
  filtrarConectores,
  contarPorStatus,
  contarPorTipo,
  contarPorCapacidade,
  alternar,
  fiscalAtivo,
  alternarFiscal,
} from "./classificacao";
import ConectorCard from "./ConectorCard";
import ConectorDrawer from "./ConectorDrawer";
import ConectorErpDrawer from "./ConectorErpDrawer";
import "./Integracoes.css";

// Catálogo de conectores do embarcador (H3, PO1-3261): PageHero + chips de filtro
// com contagem + grade de cards + painel lateral (coluna 1fr 440px) quando um card
// está aberto — mesmo padrão do Catálogo de Transportadoras. Dados: o formulário de
// parâmetros do embarcador (os 6 conectores fixos são parâmetros) + os certificados
// A1 (SEFAZ) + os conectores de ERP do catálogo (H4, PO1-3617: GET /conectores-erp,
// um card por conector de escopo embarcador, sem programar card por ERP). Se o
// catálogo falhar, a tela segue só com os 6. Sem embarcador específico →
// SpecificEmpresaRequiredNotice.
//
// Filtros (H4): situação (um só, como antes) E tipo de sistema (vários) E o que
// troca (vários; "Fiscal" liga NF-e + CT-e). Contagens de tipo/capacidade são
// sobre a lista já filtrada pela situação.
const FILTROS = ["todas", "ativa", "disponivel", "gerenciada"];

// PO1-3848 (Bling) e PO1-3853 (conectores de ERP com OAuth por conectorKey;
// 1º uso: Tiny): o retorno da autorização chega em
// /app/integracoes?conector=<key>&status=ok|erro&msg=. Lido da URL do
// navegador (não do Router: a tela não depende dele) e limpo com
// replaceState depois de tratado.
const NOMES_OAUTH = { bling: "Bling", tiny: "Tiny" };
export function lerRetornoOAuth(search) {
  const params = new URLSearchParams(search || "");
  const conector = params.get("conector");
  const status = params.get("status");
  if (!conector || !status) return null;
  return { conector, status, msg: params.get("msg") || "" };
}

const stateBoxStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.86rem",
  padding: "2rem 1rem",
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "0.6rem",
};

function ChipMulti({ ativo, onClick, children, contagem, atalho }) {
  return (
    <button
      type="button"
      className={`integracoes-chip${ativo ? " is-active" : ""}${atalho ? " integracoes-chip--atalho" : ""}`}
      onClick={onClick}
      aria-pressed={!!ativo}
    >
      {children}
      {contagem !== undefined ? <span className="integracoes-chip__count">{contagem}</span> : null}
    </button>
  );
}

export default function Integracoes() {
  const { empresaId, hasEmpresaSelection } = useCompanies();
  const { addToast } = useToast();

  const [formulario, setFormulario] = useState([]);
  const [certificados, setCertificados] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [filtro, setFiltro] = useState("todas");
  const [tipos, setTipos] = useState([]);
  const [capacidades, setCapacidades] = useState([]);
  const [selecionadoKey, setSelecionadoKey] = useState(null);

  const carregar = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    setErro("");
    try {
      // O certificado é só para o status da SEFAZ: se a listagem falhar (403/503),
      // a página segue e a SEFAZ aparece como não configurada. O catálogo de ERPs
      // falhando avisa e segue só com os 6 cards.
      const [form, certs, erps] = await Promise.all([
        getFormulario("embarcador", empresaId),
        listarCertificados().catch(() => []),
        listarConectoresErp(empresaId).catch((e) => {
          addToast({ place: "tr", type: "warning", title: "Catálogo de integrações", description: mensagemErroParametros(e, "Não foi possível carregar os conectores do catálogo (ERP e gestão de entrega).") });
          return [];
        }),
      ]);
      setFormulario(form);
      setCertificados(certs);
      setCatalogo(erps);
    } catch (e) {
      const mensagem = mensagemErroParametros(e, "Falha ao carregar os conectores.");
      setErro(mensagem);
      addToast({ place: "tr", type: "danger", title: "Erro", description: mensagem });
    } finally {
      setLoading(false);
    }
  }, [empresaId, addToast]);

  useEffect(() => {
    setSelecionadoKey(null);
    carregar();
  }, [carregar]);

  // PO1-3848/PO1-3853: depois do 1º carregamento, avisa o resultado da
  // conexão OAuth, reabre o painel do conector e limpa a query (o efeito acima zera a seleção
  // só quando `carregar` muda, então reabrir aqui não é desfeito).
  const retornoTratado = useRef(false);
  useEffect(() => {
    if (loading || retornoTratado.current) return;
    const retorno = lerRetornoOAuth(window.location.search);
    if (!retorno) return;
    retornoTratado.current = true;
    const nome = NOMES_OAUTH[retorno.conector] || retorno.conector;
    if (retorno.status === "ok") {
      addToast({ place: "tr", type: "success", title: `Conta ${nome} conectada`, description: "A conta foi autorizada. As frentes ligadas passam a rodar nos próximos ciclos." });
    } else {
      addToast({ place: "tr", type: "danger", title: `Conexão com o ${nome}`, description: retorno.msg || `Não foi possível conectar a conta ${nome}.` });
    }
    setSelecionadoKey(`catalogo:${retorno.conector}`);
    if (window.history && typeof window.history.replaceState === "function") {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.hash || ""}`);
    }
  }, [loading, addToast]);

  const cardsCatalogo = useMemo(() => catalogo.map(cardDoCatalogo), [catalogo]);
  const todos = useMemo(() => [...CONECTORES, ...cardsCatalogo], [cardsCatalogo]);

  // Status (e rótulo, nos do catálogo) por key.
  const statusPorKey = useMemo(() => {
    const mapa = {};
    CONECTORES.forEach((c) => {
      mapa[c.key] = statusDoConector(c, formulario, certificados, undefined, empresaId);
    });
    cardsCatalogo.forEach((c) => {
      mapa[c.key] = statusConectorErp(c.situacao).status;
    });
    return mapa;
  }, [formulario, certificados, empresaId, cardsCatalogo]);

  const rotuloPorKey = useMemo(() => {
    const mapa = {};
    cardsCatalogo.forEach((c) => {
      mapa[c.key] = statusConectorErp(c.situacao).rotulo;
    });
    return mapa;
  }, [cardsCatalogo]);

  const contagem = useMemo(() => contarPorStatus(todos, statusPorKey), [todos, statusPorKey]);
  const porSituacao = useMemo(() => filtrarConectores(todos, { status: filtro }, statusPorKey), [todos, filtro, statusPorKey]);
  const contagemTipo = useMemo(() => contarPorTipo(porSituacao), [porSituacao]);
  // Só mostra o tipo que tem conector no catálogo (hoje nenhum é de coleta).
  const tiposComConector = useMemo(() => {
    const total = contarPorTipo(todos);
    return Object.keys(TIPOS_SISTEMA).filter((t) => total[t] > 0);
  }, [todos]);
  const contagemCap = useMemo(() => contarPorCapacidade(porSituacao), [porSituacao]);
  const visiveis = useMemo(() => filtrarConectores(todos, { status: filtro, tipos, capacidades }, statusPorKey), [todos, filtro, tipos, capacidades, statusPorKey]);

  const selecionado = todos.find((c) => c.key === selecionadoKey) || null;
  const drawerAberto = !!selecionado;
  const temFiltroExtra = tipos.length > 0 || capacidades.length > 0;
  const limparFiltros = () => {
    setFiltro("todas");
    setTipos([]);
    setCapacidades([]);
  };

  if (!hasEmpresaSelection) {
    return (
      <div className="content integracoes-page">
        <SpecificEmpresaRequiredNotice message="Selecione um embarcador específico para ver as integrações." />
      </div>
    );
  }

  return (
    <div className="content integracoes-page">
      <PageHero
        eyebrow="Integrações"
        title="Catálogo de conectores"
        description="Sistemas que trocam documentos com o Hub: ERP, captura de notas, SEFAZ e gestão de entrega (ocorrências e canhoto). Ative, configure e teste cada conexão aqui."
        actions={<PageHeroRefreshButton onClick={carregar} loading={loading} />}
      >
        <div className="integracoes-chips" style={{ marginTop: "1rem" }} role="tablist" aria-label="Filtro de conectores">
          {FILTROS.map((f) => (
            <button
              key={f}
              type="button"
              className={`integracoes-chip${filtro === f ? " is-active" : ""}`}
              onClick={() => setFiltro(f)}
              role="tab"
              aria-selected={filtro === f}
            >
              {ROTULO_FILTRO[f]}
              <span className="integracoes-chip__count">{contagem[f]}</span>
            </button>
          ))}
        </div>

        <div className="integracoes-filtros">
          <div className="integracoes-filtros__linha" role="group" aria-label="Tipo de sistema">
            <span className="integracoes-filtros__rotulo">Tipo de sistema</span>
            {tiposComConector.map((t) => (
              <ChipMulti key={t} ativo={tipos.indexOf(t) !== -1} onClick={() => setTipos(alternar(tipos, t))} contagem={contagemTipo[t]}>
                {TIPOS_SISTEMA[t]}
              </ChipMulti>
            ))}
          </div>
          <div className="integracoes-filtros__linha" role="group" aria-label="O que troca">
            <span className="integracoes-filtros__rotulo">O que troca</span>
            {Object.keys(CAPACIDADES).map((k) => (
              <ChipMulti key={k} ativo={capacidades.indexOf(k) !== -1} onClick={() => setCapacidades(alternar(capacidades, k))} contagem={contagemCap[k]}>
                {CAPACIDADES[k]}
              </ChipMulti>
            ))}
            <ChipMulti atalho ativo={fiscalAtivo(capacidades)} onClick={() => setCapacidades(alternarFiscal(capacidades))} contagem={contagemCap.nfe + contagemCap.cte > 0 ? porSituacao.filter((c) => (c.capacidades || []).some((cap) => cap === "nfe" || cap === "cte")).length : 0}>
              Fiscal
            </ChipMulti>
          </div>
          {temFiltroExtra ? (
            <div className="integracoes-filtros__linha">
              <span className="integracoes-filtros__rotulo" />
              <button type="button" className="integracoes-filtros__limpar" onClick={() => { setTipos([]); setCapacidades([]); }}>
                Limpar filtros
              </button>
            </div>
          ) : null}
        </div>
      </PageHero>

      <div className={`integracoes-layout${drawerAberto ? " has-drawer" : ""}`}>
        <ShellCard
          title="Conectores"
          description={
            loading
              ? "Carregando..."
              : `${visiveis.length} conector${visiveis.length === 1 ? "" : "es"}${filtro === "todas" && !temFiltroExtra ? " no catálogo" : filtro === "todas" ? " no filtro" : ` · ${ROTULO_FILTRO[filtro].toLowerCase()}`}`
          }
          style={{ marginTop: 0 }}
        >
          {loading ? (
            <div style={stateBoxStyle}>
              <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} />
              <span>Carregando conectores...</span>
            </div>
          ) : erro ? (
            <div style={stateBoxStyle}>
              <span style={{ color: AUTOPILOT_COLORS.statusDanger }}>{erro}</span>
              <Button size="sm" color="info" outline onClick={carregar}>
                Tentar novamente
              </Button>
            </div>
          ) : visiveis.length ? (
            <div className="integracoes-grid">
              {visiveis.map((c) => (
                <ConectorCard
                  key={c.key}
                  conector={c}
                  status={statusPorKey[c.key]}
                  rotuloStatus={rotuloPorKey[c.key]}
                  aberto={selecionadoKey === c.key}
                  onAbrir={() => setSelecionadoKey(c.key)}
                />
              ))}
            </div>
          ) : (
            <div style={stateBoxStyle}>
              <span>Nenhum conector neste filtro.</span>
              <Button size="sm" color="info" outline onClick={limparFiltros}>
                Ver todos
              </Button>
            </div>
          )}
        </ShellCard>

        {drawerAberto && selecionado.origem === "catalogo" ? (
          <ConectorErpDrawer conector={selecionado} embarcadorId={empresaId} onClose={() => setSelecionadoKey(null)} onSalvo={carregar} />
        ) : drawerAberto ? (
          <ConectorDrawer
            conector={selecionado}
            status={statusPorKey[selecionado.key]}
            formulario={formulario}
            certificados={certificados}
            embarcadorId={empresaId}
            onClose={() => setSelecionadoKey(null)}
            onSalvo={carregar}
          />
        ) : null}
      </div>
    </div>
  );
}
